import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../lib/apiClient";
import { useAppAuth } from "../contexts/AuthContext";
import type {
  AttentionItem,
  ClerkSnapshot,
  EnforcementHealth,
  Overview,
  ProvisionSummary,
  SyncAction,
  SyncRun,
} from "../lib/types";
import { RunStatus, RunsTable } from "../components/runs";
import {
  ExcludeButton,
  useExclusions,
  type ExclusionsApi,
} from "../components/exclusions";
import { fmtSgtDateTime } from "../lib/datetime";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export default function DashboardPage() {
  const { request } = useApi();
  const { appUser } = useAppAuth();
  const isAdmin = appUser?.role === "admin";

  // Only admins may read/write the allowlist — skip the fetch for plain users.
  const ex = useExclusions(isAdmin);

  const [ov, setOv] = useState<Overview | null>(null);
  const [snap, setSnap] = useState<ClerkSnapshot | null>(null);
  const [snapErr, setSnapErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  // Latest-run detail popup.
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailActions, setDetailActions] = useState<SyncAction[] | null>(null);
  const [detailErr, setDetailErr] = useState<string | null>(null);

  const loadOverview = useCallback(async () => {
    try {
      setOv(await request<Overview>("/api/dashboard/overview"));
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [request]);

  const loadSnapshot = useCallback(async () => {
    setSnapErr(null);
    try {
      setSnap(await request<ClerkSnapshot>("/api/dashboard/clerk-snapshot"));
    } catch (e) {
      setSnapErr(e instanceof Error ? e.message : String(e));
    }
  }, [request]);

  useEffect(() => {
    void loadOverview();
    void loadSnapshot();
  }, [loadOverview, loadSnapshot]);

  const runNow = async (overrideThreshold = false) => {
    setRunning(true);
    setErr(null);
    try {
      // The endpoint returns immediately (202) with a run in "running" state;
      // poll its status so a slow aggregator window can't time out the request.
      const { run } = await request<{ run: SyncRun }>("/api/sync/run", {
        method: "POST",
        body: JSON.stringify({ overrideThreshold }),
      });
      for (let i = 0; i < 90; i++) {
        const { run: latest } = await request<{ run: SyncRun }>(
          `/api/sync/runs/${run.id}`,
        );
        if (latest.status !== "running") break;
        await sleep(2000);
      }
      await Promise.all([loadOverview(), loadSnapshot()]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  const openDetails = async () => {
    if (!ov?.latestRun) return;
    setDetailOpen(true);
    setDetailActions(null);
    setDetailErr(null);
    try {
      const data = await request<{ actions: SyncAction[] }>(
        `/api/sync/runs/${ov.latestRun.id}`,
      );
      setDetailActions(data.actions);
    } catch (e) {
      setDetailErr(e instanceof Error ? e.message : String(e));
    }
  };

  if (err && !ov) return <div className="page error">{err}</div>;
  if (!ov) return <div className="page">Loading…</div>;

  const s = ov.latestRun?.summary;
  const dryRun = ov.scheduler.dryRun;
  const pending = s?.pendingTermination ?? [];

  return (
    <div className="page">
      <div className="dash-head">
        <h1>Dashboard</h1>
        {isAdmin && (
          <button onClick={() => void runNow()} disabled={running || ov.running}>
            {running || ov.running ? "Syncing…" : "Run sync now"}
          </button>
        )}
      </div>
      {err && <p className="error">{err}</p>}

      {isAdmin && ov.latestRun?.status === "aborted_threshold" && (
        <ThresholdOverride
          run={ov.latestRun}
          busy={running || ov.running}
          onConfirm={() => void runNow(true)}
        />
      )}

      <div className={`banner ${dryRun ? "banner-warn" : "banner-ok"}`}>
        <strong>{dryRun ? "DRY-RUN mode — nothing is being locked" : "ENFORCING"}</strong>{" "}
        — scheduled <code>{ov.scheduler.cron}</code> ({ov.scheduler.timezone}).
        Lock strategy: {ov.scheduler.lockStrategy}. Guard:{" "}
        {ov.scheduler.maxLockPercent}% (floor {ov.scheduler.thresholdMinUsers}).
        {!ov.scheduler.enabled && " Scheduler DISABLED."}
      </div>

      <div
        className={`banner ${
          ov.scheduler.provisionMode === "enforce" ? "banner-ok" : "banner-warn"
        }`}
      >
        <strong>
          {ov.scheduler.provisionMode === "enforce"
            ? "CREATING ACCOUNTS"
            : ov.scheduler.provisionMode === "dry_run"
              ? "Account creation: DRY-RUN — deciding but not creating"
              : "Account creation: OFF"}
        </strong>{" "}
        — staff-level employees with no Clerk account.
        {ov.scheduler.provisionMode !== "off" && (
          <>
            {" "}
            Max {ov.scheduler.provisionMaxPerRun} per run; only{" "}
            {ov.scheduler.provisionDomains.length
              ? ov.scheduler.provisionDomains.map((d) => `@${d}`).join(", ")
              : "any domain"}
            .
          </>
        )}
      </div>

      <EnforcementBanner health={ov.enforcement} />
      <AttentionPanel items={ov.attention} ex={isAdmin ? ex : null} />

      <HowItWorks scheduler={ov.scheduler} />

      {ov.latestRun ? (
        <>
          <h2>
            Latest run <RunStatus run={ov.latestRun} />{" "}
            <Link to={`/runs/${ov.latestRun.id}`} className="muted">
              details →
            </Link>
          </h2>
          <p className="muted">
            {fmtSgtDateTime(ov.latestRun.startedAt)}
            {ov.latestRun.finishedAt
              ? ` → ${fmtSgtDateTime(ov.latestRun.finishedAt)}`
              : ""}
          </p>
          <p className="muted" style={{ marginTop: "-0.5rem" }}>
            Click any card to see the affected users.
          </p>
          <div className="cards cards-clickable" onClick={openDetails}>
            <Stat label="Clerk users" value={s?.totalClerkUsers} />
            <Stat label="Matched" value={s?.matched} />
            <Stat
              label={ov.latestRun.dryRun ? "Would lock" : "Locked"}
              value={ov.latestRun.dryRun ? s?.wouldLock : s?.locked}
              highlight
            />
            <Stat label="Kept active" value={s?.activeKept} />
            <Stat label="Already locked" value={s?.alreadyLocked} />
            <Stat label="Errors" value={s?.errors} />
          </div>
          {s?.provision && s.provision.mode !== "off" && (
            <ProvisionPanel p={s.provision} runId={ov.latestRun.id} />
          )}
          {s && (
            <>
              <h3>Employees by status</h3>
              <ul className="pills">
                {Object.entries(s.employeesByStatus).map(([k, v]) => (
                  <li key={k}>
                    {k}: <strong>{v}</strong>
                  </li>
                ))}
              </ul>
              {!!s.employeesWithoutEmail && (
                <p className="muted">
                  {s.employeesWithoutEmail} employee
                  {s.employeesWithoutEmail === 1 ? " has" : "s have"} no email in
                  HR
                  {s.linkedMatched
                    ? ` — ${s.linkedMatched} login${s.linkedMatched === 1 ? " is" : "s are"} linked to one`
                    : " and cannot be matched to a login until an admin links them"}
                  . {isAdmin && <Link to="/settings">Manage links →</Link>}
                </p>
              )}
              {!!s.linkedMissing && (
                <p className="error">
                  {s.linkedMissing} allowlist link
                  {s.linkedMissing === 1 ? "" : "s"} point at an employee who is
                  no longer in HR — those logins were locked.{" "}
                  {isAdmin && <Link to="/settings">Review links →</Link>}
                </p>
              )}
              {pending.length > 0 && (
                <details className="detail-block">
                  <summary>
                    {pending.length} employee{pending.length === 1 ? "" : "s"}{" "}
                    pending termination (serving notice — kept until last day)
                  </summary>
                  <table>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Last working day (SGT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pending.map((p, i) => (
                        <tr key={i}>
                          <td>{p.email}</td>
                          <td>{p.lastWorkingDay ?? "— (not set)"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
              {s.sourceStale ? (
                <p className="error">
                  <strong>HR data is stale</strong> — the mirror was last synced{" "}
                  {fmtSgtDateTime(s.sourceSyncedAt)}
                  {s.sourceAgeHours
                    ? ` (${s.sourceAgeHours.toFixed(0)}h ago)`
                    : ""}
                  . Anyone who left since then still has access. Check the HR
                  export before trusting this run.
                </p>
              ) : (
                <p className="muted">
                  Employee data synced: {fmtSgtDateTime(s.sourceSyncedAt)} (daily
                  mirror)
                </p>
              )}
            </>
          )}
        </>
      ) : (
        <p className="muted">No syncs yet.</p>
      )}

      <h2>Clerk snapshot (live)</h2>
      {snapErr && <p className="error">Snapshot unavailable: {snapErr}</p>}
      {snap ? (
        <>
          <div className="cards">
            <Stat label="Total users" value={snap.total} />
            <Stat label="Banned" value={snap.banned} />
            <Stat label="Locked" value={snap.locked} />
            <Stat label="Active (12h)" value={snap.active12hCount} highlight />
          </div>
          {snap.active12h.length > 0 && (
            <details>
              <summary>{snap.active12h.length} active in the last 12h</summary>
              <ul>
                {snap.active12h.map((u, i) => (
                  <li key={i}>
                    {u.email ?? "(no email)"} —{" "}
                    {fmtSgtDateTime(u.lastActiveAt)}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <p className="muted">Snapshot taken: {fmtSgtDateTime(snap.generatedAt)}</p>
        </>
      ) : (
        !snapErr && <p className="muted">Loading Clerk snapshot…</p>
      )}

      <h2>Recent runs</h2>
      <RunsTable runs={ov.runs} />

      {detailOpen && ov.latestRun && (
        <DetailModal
          run={ov.latestRun}
          actions={detailActions}
          error={detailErr}
          ex={isAdmin ? ex : null}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * A failed run is loud; a run that never *starts* is silent. This turns that
 * silence into something visible — a wedged scheduler, a dead credential or a
 * crash-looping container all look perfectly healthy until you notice that
 * nothing has actually been enforced in a day.
 */
function EnforcementBanner({ health }: { health: EnforcementHealth }) {
  if (!health.enforcing) return null;
  const age =
    health.hoursSince === null
      ? "never"
      : `${health.hoursSince.toFixed(1)}h ago`;
  if (!health.stale) {
    return (
      <p className="muted" style={{ marginTop: "-0.75rem" }}>
        Last enforcing run: {fmtSgtDateTime(health.lastEnforcedAt)} ({age}).
      </p>
    );
  }
  return (
    <div className="banner banner-err">
      <strong>ENFORCEMENT STALE</strong> — no completed enforcing run in the last{" "}
      {health.maxQuietHours}h (last:{" "}
      {health.lastEnforcedAt ? fmtSgtDateTime(health.lastEnforcedAt) : "never"}).
      Leavers are not being locked out. Check the scheduler, the HR API key and
      the container logs.
    </div>
  );
}

/**
 * The 20% guard stops a bad HR pull locking everyone out — but a *genuine* mass
 * termination trips it too, and then enforcement is stuck with no way forward.
 * This is the way forward: an admin reads the numbers and approves the run,
 * which is recorded against it.
 */
function ThresholdOverride({
  run,
  busy,
  onConfirm,
}: {
  run: SyncRun;
  busy: boolean;
  onConfirm: () => void;
}) {
  const [armed, setArmed] = useState(false);
  const s = run.summary;
  const pct = s ? (s.threshold.ratio * 100).toFixed(1) : "?";
  return (
    <div className="banner banner-warn">
      <strong>Last run stopped at the {s?.threshold.percent ?? 20}% guard</strong>{" "}
      — it would have locked <strong>{s?.wouldLock ?? "?"}</strong> of{" "}
      {s?.totalClerkUsers ?? "?"} accounts ({pct}%), so it locked nothing.
      <p style={{ margin: "0.5rem 0 0" }}>
        Open the run and check the list. If those really have all left, you can
        approve a run that ignores the guard — your approval is recorded on it.
        If the list looks wrong, fix the HR data instead; the guard just did its
        job.
      </p>
      <p style={{ margin: "0.5rem 0 0" }}>
        <Link to={`/runs/${run.id}`}>Review the {s?.wouldLock ?? 0} accounts →</Link>
      </p>
      <div className="actions" style={{ marginTop: "0.6rem" }}>
        {armed ? (
          <>
            <button disabled={busy} onClick={onConfirm}>
              Yes — lock them now
            </button>
            <button disabled={busy} onClick={() => setArmed(false)}>
              Cancel
            </button>
          </>
        ) : (
          <button disabled={busy} onClick={() => setArmed(true)}>
            I've reviewed this — run anyway
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Subjects the sync cannot decide on: conflicting namesakes, and Clerk logins
 * with no usable email. Both fail safe — nobody is locked on a guess — but
 * failing safe twice a day forever is a hole, not a resolution. Showing the
 * streak is what turns a recurring line in a long list into something with an
 * obvious age.
 */
function AttentionPanel({
  items,
  ex,
}: {
  items: AttentionItem[];
  ex: ExclusionsApi | null;
}) {
  if (items.length === 0) return null;
  return (
    <div className="banner banner-warn">
      <strong>
        {items.length} account{items.length === 1 ? "" : "s"} need
        {items.length === 1 ? "s" : ""} attention
      </strong>{" "}
      — the sync could not decide about these, so it left them alone. They are
      neither locked nor confirmed safe.
      <table style={{ marginTop: "0.6rem" }}>
        <thead>
          <tr>
            <th>Email</th>
            <th>Problem</th>
            <th>Why</th>
            <th>Unresolved for</th>
            {ex && <th>Allowlist</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.clerkUserId ?? a.email ?? Math.random()}>
              <td>{a.email ?? a.clerkUserId ?? "—"}</td>
              <td>
                {a.outcome === "ambiguous"
                  ? "two HR records disagree"
                  : "no usable email on the login"}
              </td>
              <td>{a.reason ?? "—"}</td>
              <td>
                {a.runs} run{a.runs === 1 ? "" : "s"} · since{" "}
                {fmtSgtDateTime(a.since)}
              </td>
              {ex && (
                <td className="actions">
                  <ExcludeButton email={a.email} ex={ex} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The additive half of a run. Deliberately states the denominators as well as
 * the count: "12 created" reads very differently once you know it is 12 of 328
 * and the rest arrive over the following runs.
 */
function ProvisionPanel({ p, runId }: { p: ProvisionSummary; runId: string }) {
  if (!p.staffWorkerAvailable) {
    return (
      <p className="error">
        Account creation is configured but the HR data source is not exposing
        the staff/worker field, so staff-level employees cannot be identified.
        Nothing was created.
      </p>
    );
  }
  const enforcing = p.mode === "enforce";
  return (
    <>
      <h3>
        Accounts{" "}
        {!enforcing && <span className="muted">(dry-run — nothing created)</span>}
      </h3>
      <div className="cards">
        <Stat label="Active staff" value={p.staffActive} />
        <Stat label="Already have an account" value={p.alreadyHaveAccount} />
        <Stat
          label={enforcing ? "Created" : "Would create"}
          value={enforcing ? p.created : p.wouldCreate}
          highlight
        />
        <Stat label="Still to create" value={p.deferredByCap} />
        <Stat label="Errors" value={p.createErrors} />
      </div>
      {p.deferredByCap > 0 && (
        <p className="muted">
          {p.eligible} staff need an account. This run handles up to {p.cap};
          the remaining {p.deferredByCap} follow on the next runs.{" "}
          <Link to={`/runs/${runId}`}>See the list →</Link>
        </p>
      )}
      {p.skippedDeletedBefore > 0 && (
        <p className="muted">
          {p.skippedDeletedBefore} account
          {p.skippedDeletedBefore === 1 ? " was" : "s were"} created by an
          earlier run and later deleted in Clerk — deliberate deletions are
          never undone, so {p.skippedDeletedBefore === 1 ? "it was" : "they were"}{" "}
          not recreated.
        </p>
      )}
      {p.staffWithoutEmail.length > 0 && (
        <p className="error">
          {p.staffWithoutEmail.length} active staff have no email address in HR
          and cannot be given an account:{" "}
          {p.staffWithoutEmail.map((e) => e.employeeNo ?? "?").join(", ")}. Add
          their addresses in EMS.
        </p>
      )}
      {p.badDomain.length > 0 && (
        <p className="error">
          {p.badDomain.length} active staff have an email outside the permitted
          domains — likely a typo in HR:{" "}
          {p.badDomain
            .map((b) => `${b.employeeNo ?? "?"} (${b.email})`)
            .join(", ")}
        </p>
      )}
    </>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number | undefined;
  highlight?: boolean;
}) {
  return (
    <div className={`card ${highlight ? "card-hl" : ""}`}>
      <div className="card-value">{value ?? "—"}</div>
      <div className="card-label">{label}</div>
    </div>
  );
}

/** Popup listing the users the latest run acted on (or would act on). */
function DetailModal({
  run,
  actions,
  error,
  ex,
  onClose,
}: {
  run: SyncRun;
  actions: SyncAction[] | null;
  error: string | null;
  /** Allowlist controls; null for non-admins (no per-row column). */
  ex: ExclusionsApi | null;
  onClose: () => void;
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3 style={{ margin: 0 }}>
            {run.dryRun ? "Would-be actions" : "Actions"} — latest run
          </h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <p className="muted">
          {fmtSgtDateTime(run.startedAt)} · {run.trigger} ·{" "}
          {run.dryRun ? "dry-run" : "enforcing"}
        </p>
        {ex && (
          <p className="muted">
            <strong>Never lock</strong> adds the email to the allowlist so future
            runs skip it. It does not unlock anyone already locked — that is a
            manual re-grant. Manage the list in{" "}
            <Link to="/settings">Settings</Link>.
          </p>
        )}
        {error && <p className="error">{error}</p>}
        {ex?.error && <p className="error">{ex.error}</p>}
        {!error && !actions && <p className="muted">Loading…</p>}
        {actions && actions.length === 0 && (
          <p className="muted">
            No notable actions — every matched user was kept active.
          </p>
        )}
        {actions && actions.length > 0 && (
          <div className="modal-scroll">
            <table>
              <thead>
                <tr>
                  <th>Clerk email</th>
                  <th>Outcome</th>
                  <th>Employment status</th>
                  <th>Last working day</th>
                  <th>Why</th>
                  {ex && <th>Allowlist</th>}
                </tr>
              </thead>
              <tbody>
                {actions.map((a) => (
                  <tr key={a.id}>
                    <td>{a.canonicalEmail ?? a.email ?? "—"}</td>
                    <td>{a.outcome}</td>
                    <td>{a.employmentStatus ?? "—"}</td>
                    <td>{a.lastWorkingDay ?? "—"}</td>
                    <td>
                      {a.reason ?? "—"}
                      {a.viaEmployeeNo && (
                        <span className="muted"> (via employee #{a.viaEmployeeNo})</span>
                      )}
                    </td>
                    {ex && (
                      <td className="actions">
                        <ExcludeButton
                          email={a.canonicalEmail ?? a.email}
                          ex={ex}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/** Plain-language explanation of the schedule and the lock rules. */
function HowItWorks({ scheduler }: { scheduler: Overview["scheduler"] }) {
  return (
    <details className="detail-block explain">
      <summary>How this works (plain English)</summary>
      <div className="explain-body">
        <p>
          This tool keeps single sign-on honest: it makes sure only current
          employees can log into the company apps. It compares the HR employee
          list against everyone who has a login account, and <strong>locks the
          accounts of people who have left</strong>. Locking someone here signs
          them out of <em>every</em> connected app within about a minute.
        </p>
        <p>
          <strong>When it runs:</strong> automatically twice a day, at{" "}
          <strong>6:00 AM and 6:00 PM Singapore time</strong>{" "}
          (<code>{scheduler.cron}</code>, {scheduler.timezone}). An admin can
          also press <strong>“Run sync now”</strong> at any time. Every run
          emails a summary to the admin mailbox.
        </p>
        <p>
          <strong>Who gets locked:</strong> anyone whose HR record is{" "}
          <em>not</em> “Employed” or “Pending Termination” — for example
          Terminated staff, or someone whose login exists but has no HR record
          at all.
        </p>
        <p>
          <strong>Who is kept:</strong> current employees, and people serving
          notice (“Pending Termination”) right up until their last working day.
          On the last working day itself they keep access until{" "}
          <strong>5:30 PM SGT</strong>; the 6:00 PM run is the one that locks
          them. From the next day they are locked.
        </p>
        <p>
          <strong>Safety rails:</strong>
        </p>
        <ul>
          <li>
            <strong>Dry-run:</strong> when in dry-run mode the tool only{" "}
            <em>reports</em> what it would do and changes nothing — used to
            review before going live.
          </li>
          <li>
            <strong>{scheduler.maxLockPercent}% limit:</strong> if a single run
            would lock more than {scheduler.maxLockPercent}% of all accounts
            (and at least {scheduler.thresholdMinUsers}), it stops and alerts an
            admin instead — a guard against bad data locking everyone out.
          </li>
          <li>
            <strong>Never-lock allowlist:</strong> admins can mark an email as
            never-to-be-locked — for shared mailboxes, contractors and service
            accounts that have no HR record. Add one in{" "}
            <strong>Settings</strong>, or press <strong>Never lock</strong> next
            to any email in a run's list. App admins are always exempt.
          </li>
          <li>
            <strong>Never auto-unlocks:</strong> the tool only locks. Restoring
            access after a rehire is always done manually by an admin — even
            after adding someone to the allowlist.
          </li>
          <li>
            <strong>Same person, plus-addressed emails</strong> (e.g.{" "}
            <code>staff@</code> and <code>staff+test@</code>) are treated as one
            — locking one locks the other.
          </li>
        </ul>
        <p className="muted">
          Note: HR data comes from a mirror that refreshes about once a day, so
          a change made in HR today may take up to ~24 hours (plus the next
          6 AM/6 PM run) to take effect.
        </p>
      </div>
    </details>
  );
}
